import { X, Printer } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import AuthenticatedImage from "@/components/AuthenticatedImage";

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Indian numbering system: number to words (Crore/Lakh/Thousand/Hundred)
function numToWordsIndian(num) {
  if (num === null || num === undefined || isNaN(num)) return "Zero";
  if (num < 0) return "Minus " + numToWordsIndian(-num);
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  const three = (n) => {
    let out = "";
    if (n >= 100) { out += ones[Math.floor(n / 100)] + " Hundred"; if (n % 100) out += " "; }
    if (n % 100) out += two(n % 100);
    return out;
  };
  let rupees = Math.floor(num);
  let paise = Math.round((num - rupees) * 100);
  if (paise >= 100) { rupees += 1; paise = 0; }
  let words = "Zero";
  if (rupees > 0) {
    const crore = Math.floor(rupees / 10000000);
    const lakh = Math.floor(rupees / 100000) % 100;
    const thousand = Math.floor(rupees / 1000) % 100;
    const remainder = rupees % 1000;
    const parts = [];
    if (crore) parts.push(two(crore) + " Crore");
    if (lakh) parts.push(two(lakh) + " Lakh");
    if (thousand) parts.push(two(thousand) + " Thousand");
    if (remainder) parts.push(three(remainder));
    words = parts.join(" ").trim();
  }
  let out = `Rupees ${words}`;
  if (paise) out += ` and ${two(paise)} Paise`;
  return out + " Only";
}

export default function PayslipView({ payslip, employee, onClose }) {
  const [company, setCompany] = useState(null);
  const [meta, setMeta] = useState({ dept: "—", desig: "—" });

  useEffect(() => {
    api.get(`/companies/${employee?.company_id || "company"}`).then((r) => setCompany(r.data)).catch(() => {
      api.get("/company", { params: { company_id: employee?.company_id || "company" } }).then((r) => setCompany(r.data)).catch(() => {});
    });
  }, [employee?.company_id]);

  useEffect(() => {
    const depId = employee?.employment?.department_id;
    const desId = employee?.employment?.designation_id;
    Promise.all([
      depId ? api.get("/departments").then((r) => r.data.find((d) => d.id === depId)?.name).catch(() => null) : null,
      desId ? api.get("/designations").then((r) => r.data.find((d) => d.id === desId)?.title).catch(() => null) : null,
    ]).then(([dept, desig]) => setMeta({ dept: dept || "—", desig: desig || "—" }));
  }, [employee]);

  if (!payslip) return null;

  const print = () => window.print();
  const companyAddress = [company?.address_line1, company?.city, company?.state, company?.pincode]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print:bg-white print:p-0">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col print:max-h-none print:shadow-none print:rounded-none">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb] print:hidden">
          <h2 className="text-lg font-semibold text-slate-800">Payslip · {payslip.period_label}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={print}
              data-testid="payslip-print-btn"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50"
            >
              <Printer className="h-4 w-4" /> Print
            </button>
            <button
              onClick={onClose}
              data-testid="payslip-close-btn"
              className="p-2 text-slate-400 hover:bg-slate-100 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 text-sm" data-testid="payslip-body">
          {/* Header with company */}
          <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4">
            <div className="flex items-center gap-4">
              {company?.logo_url && (
                <AuthenticatedImage src={company.logo_url} alt="" className="h-14 w-14 object-contain" />
              )}
              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  {company?.name || "Company Name"}
                </h1>
                <p className="text-xs text-slate-600">
                  {companyAddress}
                </p>
                <p className="text-xs text-slate-600">
                  GSTIN: {company?.gstin || "—"} · PAN: {company?.pan || "—"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-slate-800">PAYSLIP</div>
              <div className="text-xs text-slate-600">For the month of {payslip.period_label}</div>
            </div>
          </div>

          {/* Employee meta */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 py-5 border-b border-slate-200">
            <Meta label="Employee Name" value={payslip.employee_name} />
            <Meta label="Employee Code" value={payslip.employee_code} />
            <Meta label="Designation" value={meta.desig} />
            <Meta label="Department" value={meta.dept} />
            <Meta label="PAN" value={employee?.salary?.pan || "—"} />
            <Meta label="Bank Account" value={employee?.salary?.account_number || "—"} />
            <Meta label="UAN" value={employee?.salary?.uan || "—"} />
            <Meta label="IFSC" value={employee?.salary?.ifsc || "—"} />
            <Meta label="PF Number" value={employee?.salary?.pf_number || "—"} />
            <Meta label="Bank Name" value={employee?.salary?.bank_name || "—"} />
            <Meta label="Working Days" value={payslip.working_days} />
            <Meta label="Paid Days" value={payslip.paid_days} />
            <Meta label="LOP Days" value={payslip.lop_days} />
          </div>

          {/* Earnings & Deductions */}
          <div className="grid grid-cols-2 gap-6 py-5">
            <div>
              <h3 className="font-bold text-slate-800 mb-2 border-b border-slate-300 pb-1">
                Earnings
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  {payslip.earnings.map((e, i) => (
                    <tr key={`earn-${e.name}-${i}`} className="border-b border-slate-100">
                      <td className="py-1.5 text-slate-700">{e.name}</td>
                      <td className="py-1.5 text-right text-slate-800">{fmtINR(e.amount)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-slate-50">
                    <td className="py-2 px-1 text-slate-800">Gross Salary</td>
                    <td className="py-2 px-1 text-right text-emerald-700">
                      {fmtINR(payslip.gross_earnings)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="font-bold text-slate-800 mb-2 border-b border-slate-300 pb-1">
                Deductions
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  {payslip.deductions.map((d, i) => (
                    <tr key={`ded-${d.name}-${i}`} className="border-b border-slate-100">
                      <td className="py-1.5 text-slate-700">
                        {d.name}
                        {d.name === "TDS" && payslip.tds_rate ? (
                          <span className="text-xs text-slate-500"> ({payslip.tds_rate}%)</span>
                        ) : null}
                      </td>
                      <td className="py-1.5 text-right text-slate-800">{fmtINR(d.amount)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold bg-slate-50">
                    <td className="py-2 px-1 text-slate-800">Total Deductions</td>
                    <td className="py-2 px-1 text-right text-rose-600">
                      {fmtINR(payslip.total_deductions)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Net pay */}
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase font-semibold text-emerald-700 tracking-wider">
                  Net Salary for {payslip.period_label}
                </div>
                <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="payslip-net-pay">
                  {fmtINR(payslip.net_pay)}
                </div>
              </div>
              <div className="text-xs text-slate-500 max-w-[200px] text-right">
                
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-emerald-200 text-sm" data-testid="payslip-net-pay-words">
              <span className="font-semibold text-slate-700">In words: </span>
              <span className="italic text-slate-700">{numToWordsIndian(payslip.net_pay)}</span>
            </div>
          </div>

          <div className="text-xs text-slate-500 mt-3 italic text-center print:mt-6">
            This is a computer-generated payslip and does not require a signature.
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-[#f9fafb] flex justify-end print:hidden">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 rounded bg-white hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const Meta = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">{label}</div>
    <div className="text-sm text-slate-800">{value || "—"}</div>
  </div>
);
